// Native-OS folder picker via tauri-plugin-dialog.
//
// Returns a discriminated union so the caller can show the user a meaningful
// state for each outcome — silently returning null hid real failures in
// production (the Tauri lazy-chunk fetch was failing without surfacing).
//
// We use a STATIC import for the dialog plugin. The plugin is tiny (~1.5 KB)
// and was previously dynamic-imported to avoid loading it in browser dev
// mode, but Vite then code-split it into a separate chunk and Tauri's asset
// protocol intermittently failed to fetch that chunk — the user saw the
// button doing nothing because the import promise silently rejected.
// Static import keeps the code in the main bundle and makes the call
// synchronous after window-readiness.

import { open as tauriOpen } from "@tauri-apps/plugin-dialog";

export type PickFolderResult =
  | { kind: "ok"; path: string }
  | { kind: "cancelled" }
  | { kind: "no-tauri" }
  | { kind: "error"; message: string };

function detectTauri(): boolean {
  return Boolean((window as unknown as { __TAURI_INTERNALS__?: unknown }).__TAURI_INTERNALS__);
}

export async function pickFolder(): Promise<PickFolderResult> {
  if (!detectTauri()) return { kind: "no-tauri" };
  try {
    const result = await tauriOpen({
      directory: true,
      multiple: false,
      title: "Choose project folder",
    });
    if (typeof result === "string" && result.length > 0) {
      return { kind: "ok", path: result };
    }
    return { kind: "cancelled" };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    // Surface in console for devtools, but also bubble up so the UI can
    // render the error — silent swallow was the original bug.
    console.error("[folderPicker] dialog open failed:", err);
    return { kind: "error", message };
  }
}
